import { useQuery } from "@tanstack/react-query";

import { workflowStatusQueryKeys } from "./queryKeys";

import { instance } from "@/lib/axios/instance";

export interface CockpitWorkflowStatus {
  id: string;
  name: string | null;
}

interface WorkflowStatusesApiResponse {
  data: CockpitWorkflowStatus[];
  totalRecords: number;
}

// Raw fetch for a module's Cockpit workflow statuses. The module's backend strategy selects the
// status type (candidate-tracking → Candidates, job-source → Publications), so callers pass only the
// module — no status-type string. Shared by the react-query hook and AsyncSelect's fetcher.
export async function fetchCockpitWorkflowStatuses(
  clientId: string,
  moduleName: string
): Promise<CockpitWorkflowStatus[]> {
  const { data } = await instance.get<WorkflowStatusesApiResponse>(
    `/api/v2/Integrations/cockpit/${moduleName}/workflow-statuses`,
    { params: { clientId } }
  );
  return data?.data ?? [];
}

// Cached (moduleName, clientId) list — used where react-query caching is wanted (e.g. the publications tab).
export function useCockpitWorkflowStatuses(
  clientId: string | undefined,
  moduleName: string
) {
  return useQuery({
    queryKey: workflowStatusQueryKeys.list(clientId, moduleName),
    queryFn: () => fetchCockpitWorkflowStatuses(clientId as string, moduleName),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
}
